import React from 'react';
import { FiSend } from 'react-icons/fi';
import { HiUser, HiEnvelope, HiPhone, HiChatBubbleLeftEllipsis, HiPencilSquare } from 'react-icons/hi2';
import { Card } from '../../components/ui/Card';
import { useLanguage } from '../../contexts/LanguageContext';
import { FormField } from '../../components/ui/FormField';
import { Input } from '../../components/ui/Input';
import { Textarea } from '../../components/ui/Textarea';
import { Button } from '../../components/ui/Button';
import { Spinner } from '../../components/ui/Spinner';

/**
 * Contact form component
 *
 * @param {Object} props
 * @param {Object} props.form - Form values
 * @param {Function} props.onChange - Change handler
 * @param {Function} props.onSubmit - Submit handler
 * @param {boolean} props.isSubmitting - Submitting state
 */
export const ContactForm = ({ form, onChange, onSubmit, isSubmitting }) => {
  const { t } = useLanguage();

  return (
    <Card className="h-full">
      <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-6 border-b-2 border-primary pb-2 inline-block">
        {t('contact.formTitle')}
      </h2>

      <form onSubmit={onSubmit} noValidate className="space-y-4">


        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormField label={t('contact.nameLabel')} htmlFor="name">
            <Input
              id="name"
              name="name"
              value={form.name}
              onChange={onChange}
              required
              placeholder={t('contact.namePlaceholder')}
              icon={HiUser}
            />
          </FormField>

          <FormField label={t('contact.emailLabel')} htmlFor="email">
            <Input
              id="email"
              name="email"
              type="email"
              value={form.email}
              onChange={onChange}
              required
              placeholder={t('contact.emailPlaceholder')}
              icon={HiEnvelope}
            />
          </FormField>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormField label={t('contact.phoneLabel')} htmlFor="phone">
            <Input
              id="phone"
              name="phone"
              type="tel"
              value={form.phone}
              onChange={onChange}
              placeholder={t('contact.phonePlaceholder')}
              icon={HiPhone}
            />
          </FormField>

          <FormField label={t('contact.subjectLabel')} htmlFor="subject">
            <Input
              id="subject"
              name="subject"
              value={form.subject}
              onChange={onChange}
              required
              placeholder={t('contact.subjectPlaceholder')}
              icon={HiChatBubbleLeftEllipsis}
            />
          </FormField>
        </div>

        <FormField label={t('contact.messageLabel')} htmlFor="message">
          <div className="relative">
            <div className="absolute left-3 top-4 text-gray-400 pointer-events-none">
              <HiPencilSquare size={20} />
            </div>
            <textarea
              id="message"
              name="message"
              rows="5"
              className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all resize-none"
              value={form.message}
              onChange={onChange}
              required
              placeholder={t('contact.messagePlaceholder')}
            />
          </div>
        </FormField>

        <div className="pt-4">
          <Button
            type="submit"
            disabled={isSubmitting}
            className="w-full sm:w-auto min-w-[150px] shadow-lg hover:shadow-primary/30"
          >
            {isSubmitting ? (
              <>
                <Spinner size="sm" />
                <span className="ml-2">{t('contact.sending')}</span>
              </>
            ) : (
              <>
                {t('contact.sendButton')}
                <FiSend className="ml-2" />
              </>
            )}
          </Button>
        </div>
      </form>
    </Card>
  );
};
